import { Dir } from '@angular/cdk/bidi';
import { CdkDrag, CdkDragDrop, CdkDropList, CdkDropListGroup } from '@angular/cdk/drag-drop';
import { ChangeDetectionStrategy, Component, computed, HostBinding, inject, OnDestroy, output, Signal, signal, ViewEncapsulation } from '@angular/core';
import { MatIconButton } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatAccordion, MatExpansionPanel, MatExpansionPanelHeader } from '@angular/material/expansion';
import { MatIcon } from '@angular/material/icon';
import { MatDivider } from '@angular/material/list';
import { MatMenu, MatMenuContent, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatOption, MatSelect, MatSelectChange } from '@angular/material/select';
import { MatToolbar } from '@angular/material/toolbar';
import { MatTooltip } from '@angular/material/tooltip';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { EnpsComponent } from '@common/components/base/enps-component';
import { ViewStateTypes } from '@common/enums/viewstate-types';
import { NavigationService } from '@common/services/navigation-service';
import { NomWebServicesJson } from '@common/services/nom-web-services-json';
import { VisualStateService } from '@common/services/visual-state-service';
import { Store } from '@ngrx/store';
import { AppName } from '@src/suite/common/enums/enums';
import { NavigationDragDropActions } from '@src/suite/common/enums/navigation-drag-drop-actions';
import { GlobalTablesService } from '@src/suite/common/services/global-tables-service';
import { ViewStateService } from '@src/suite/common/services/view-state-service';
import { CreateViewStateActions } from '@src/suite/common/store/createviewstate/createviewstate.actions';
import { selectPlaybookTeams } from '@src/suite/common/store/global-tables/global-tables.selectors';
import { selectHomeProgram, selectSupervisor } from '@src/suite/common/store/hello-results/hello-results.selectors';
import { selectI18N, selectTranslatedKey } from '@src/suite/common/store/i18n/selectors/i18n.selectors';
import { NavigationActions, NavigationApiActions } from '@src/suite/common/store/navigation/actions/navigation.actions';
import { NavigationGroup, NavigationGroupTypes, NavigationItem, NavigationLayoutTypes, NavigationSection, NavigationSectionTypes } from '@src/suite/common/store/navigation/models/navigation.models';
import { selectDiscoverPermissions } from '@src/suite/common/store/navigation/selectors/application.selectors';
import { selectCurrentApplication, selectCurrentTeam, selectIsWastebin, selectNavigationGroups } from '@src/suite/common/store/navigation/selectors/navigation.selectors';
import { I18nKeys } from '@src/suite/common/utils/i18n.default';
import { Util } from '@src/suite/common/utils/util';
import { AppService } from '@suite/app.service';

import { ClickStopPropagationDirective } from '../../../common/directives/stop-propagation';
import { AppSelectorComponent } from '../app-selector/app-selector';
import { SectionLabelComponent } from './section-label/section-label';

@Component({
    selector: 'st-side-bar',
    imports: [
        AppSelectorComponent,
        CdkDrag,
        CdkDropList,
        CdkDropListGroup,
        ClickStopPropagationDirective,
        Dir,
        MatAccordion,
        MatDivider,
        MatExpansionPanel,
        MatExpansionPanelHeader,
        MatIcon,
        MatIconButton,
        MatMenu,
        MatMenuContent,
        MatMenuItem,
        MatMenuTrigger,
        MatOption,
        MatSelect,
        MatToolbar,
        MatTooltip,
        RouterLink,
        RouterLinkActive,
        SectionLabelComponent,
    ],
    templateUrl: './side-bar.html',
    styleUrl: './side-bar.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'st-side-bar',
    },
})
export class SideBarComponent extends EnpsComponent implements OnDestroy {
    appService = inject(AppService);
    nomWebServicesJson = inject(NomWebServicesJson);
    navigationService = inject(NavigationService);
    visualStateService = inject(VisualStateService);
    viewStateService = inject(ViewStateService);
    viewStateDialog = inject(MatDialog);
    globalTablesService = inject(GlobalTablesService);
    private store = inject(Store);

    @HostBinding('class.st-side-bar--edit')
    editMode() {
        return this.inEditMode();
    }

    viewStateTypes = ViewStateTypes;
    navigationGroupTypes = NavigationGroupTypes;

    readonly inEditMode = signal(false);

    readonly toggleSideBar = output();

    readonly discoverPrivs = this.store.selectSignal(selectDiscoverPermissions);

    readonly groups = this.store.selectSignal(selectNavigationGroups);

    readonly application = computed(() => this.store.selectSignal(selectCurrentApplication)());

    readonly isWastebin = computed(() => this.store.selectSignal(selectIsWastebin)());
    readonly makeDefaultViewLabel = computed(() => this.store.selectSignal(selectTranslatedKey(I18nKeys.PSTWebMakeDefaultView))());
    readonly makeTeamDefaultViewLabel = computed(() => this.store.selectSignal(selectTranslatedKey(I18nKeys.PSTWebMakeTeamDefaultView))());
    readonly makeGlobalDefaultViewLabel = computed(() => this.store.selectSignal(selectTranslatedKey(I18nKeys.PSTWebMakeGlobalDefaultView))());

    languageSettings = this.store.selectSignal(selectI18N)();
    isSupervisor = this.store.selectSignal(selectSupervisor);

    readonly playbookTeams = computed(() => {
        return (
            this.store
                .selectSignal(selectPlaybookTeams)()
                .filter(team => {
                    if (this.nomWebServicesJson.HelloResults.HomeProgram === this.globalTablesService.EnterprisePlaybookGroup) {
                        return true;
                    }
                    return !team.PSTGroup;
                }) || []
        );
    });

    readonly currentTeam = signal(this.store.selectSignal(selectHomeProgram)());
    currentStoreTeam = this.store.selectSignal(selectCurrentTeam);

    isEditDisabled = (group: NavigationGroup): Signal<boolean> =>
        computed(() => {
            return !this.isSupervisor() && group.ID !== NavigationGroupTypes.Personal;
        });

    hasCreatePermissions = (group: NavigationGroup): Signal<boolean> =>
        computed(() => {
            if (this.application() === AppName.DISCOVER) {
                if (group.ID === NavigationGroupTypes.Team) {
                    if (this.discoverPrivs()?.RssTeamFeeds?.write === true) return true;
                    if (this.discoverPrivs()?.TwitterTeamFeeds?.write === true) return true;
                    if (this.discoverPrivs()?.MediaTeamFeeds?.write === true) return true;
                } else {
                    if (this.discoverPrivs()?.RssPersonalFeeds?.write === true) return true;
                    if (this.discoverPrivs()?.TwitterPersonalFeeds?.write === true) return true;
                    if (this.discoverPrivs()?.MediaPersonalFeeds?.write === true) return true;
                }
                return false;
            }
            return true;
        });

    isCreateViewStateEnabled = (group: NavigationGroup): Signal<boolean> =>
        computed(() => {
            return !this.isEditDisabled(group)() && this.hasCreatePermissions(group)();
        });

    get appName(): string {
        return this.appService?.applicationName;
    }

    readonly appSelectorVisible = computed(() => {
        return this.visualStateService?.isLessThanSmall();
    });

    get isRTL() {
        return this.globalTablesService.getRtl();
    }

    get lockUnlockTooltip(): string {
        if (this.inEditMode()) {
            return this.languageSettings.PSTWebPadLockLabel;
        }
        return this.languageSettings.PSTWebPadUnlockLabel;
    }

    onCurrentTeamChanged(event: MatSelectChange) {
        this.currentTeam.set(event.value);
        const team = this.playbookTeams().find((t: Group) => t.ID === this.currentTeam());
        if (team && team.ID !== this.currentStoreTeam()) {
            this.store.dispatch(NavigationApiActions.loadTeamNavigationData({ team: team.ID }));
        }
    }

    async createViewState(group: NavigationGroup, section: NavigationSection) {
        this.store.dispatch(CreateViewStateActions.createUpdateViewState({ group, section, edit: false, copy: false }));
    }

    async editViewState(group: NavigationGroup, section: NavigationSection, item: NavigationItem) {
        this.store.dispatch(CreateViewStateActions.createUpdateViewState({ group, section, item, edit: true, copy: false, vs: item.ViewState }));
    }

    dragEnabled(group: NavigationGroup): boolean {
        if (this.inEditMode()) {
            if (!this.isSupervisor() && group.ID !== NavigationGroupTypes.Personal) {
                return false;
            }
            return true;
        }
        return false;
    }

    public deleteViewState(_group: NavigationGroup, _section: NavigationSection, item: NavigationItem) {
        this.store.dispatch(CreateViewStateActions.deleteViewState({ vs: item.ViewState }));
    }

    makeDefaultViewState(_, item: NavigationItem) {
        this.store.dispatch(
            NavigationApiActions.setDefaultNavigationItem({
                guid: item.ViewState?.Guid,
                layout: NavigationLayoutTypes.User,
                group: NavigationGroupTypes.Personal,
                team: '',
            })
        );
    }

    makeTeamDefaultViewState(_, item: NavigationItem) {
        this.store.dispatch(
            NavigationApiActions.setDefaultNavigationItem({
                guid: item.ViewState?.Guid,
                layout: NavigationLayoutTypes.Group,
                group: NavigationGroupTypes.Team,
                team: this.currentTeam(),
            })
        );
    }

    makeGlobalDefaultViewState(_, item: NavigationItem) {
        this.store.dispatch(
            NavigationApiActions.setDefaultNavigationItem({
                guid: item.ViewState?.Guid,
                layout: NavigationLayoutTypes.Enterprise,
                group: NavigationGroupTypes.Global,
                team: '',
            })
        );
    }

    async onDrop(event: CdkDragDrop<string[]>, group: NavigationGroup, targetSection: NavigationSection) {
        const guid: string = event.item.data;
        const targetIndex: number = event.currentIndex;
        const sourceIndex: number = event.previousIndex;
        let sourceSection: NavigationSection = null;

        exit: for (const section of group.Items) {
            for (const item of section.Items) {
                if (item.Guid === guid) {
                    sourceSection = section;
                    break exit;
                }
            }
        }

        this.store.dispatch(
            NavigationActions.reorderItems({ group: group, sourceSection: sourceSection, targetSection: targetSection, sourceIndex: sourceIndex, targetIndex: targetIndex, guid: guid })
        );
    }

    async toggleEditMode() {
        this.inEditMode.set(!this.inEditMode());
    }

    createNewSection(group: NavigationGroup) {
        this.store.dispatch(
            NavigationActions.addSection({
                group: group,
                label: this.languageSettings.PSTWebNewSectionLabel,
            })
        );
    }

    renameSection(group: NavigationGroup, section: NavigationSection) {
        this.store.dispatch(
            NavigationActions.renameSection({
                group: group,
                section: section,
            })
        );
    }

    onCancelSection(group: NavigationGroup, section: NavigationSection) {
        if (section.IsNewSection) {
            this.store.dispatch(
                NavigationActions.removeSection({
                    group: group,
                    section: section,
                })
            );
        } else {
            this.store.dispatch(
                NavigationActions.cancelSection({
                    group: group,
                    section: section,
                })
            );
        }
    }

    onSaveSection(group: NavigationGroup, section: NavigationSection) {
        this.store.dispatch(
            NavigationActions.updateSection({
                group: group,
                section: {
                    ...section,
                    Path: Util.url(section.Label),
                },
            })
        );
    }

    public async removeSection(group: NavigationGroup, section: NavigationSection) {
        this.store.dispatch(
            NavigationActions.deleteSection({
                group: group,
                section: section,
            })
        );
    }

    keyPress(event: Event) {
        event.stopPropagation();
    }

    checkDropAllowed(section: NavigationSection) {
        return (dragData: any) => {
            if (!dragData) {
                return true;
            }

            const dropObj: NavigationItem = dragData.data;

            if (section.ID === NavigationSectionTypes.System) {
                return false;
            }

            let viewState: StorytellingViewStateRecord = null;
            if (dropObj && dropObj['ViewState']) {
                viewState = dropObj.ViewState;
            } else if (typeof dropObj === 'object') {
                viewState = dropObj['ViewState'] as StorytellingViewStateRecord;
            }
            if (!viewState) return;

            if (dragData.action === NavigationDragDropActions.Add) {
                if (dropObj) {
                    for (const item of section.Items) {
                        if (item.ViewState.Guid === viewState.Guid) {
                            return false;
                        }
                    }
                }
            } else if (dragData.action === NavigationDragDropActions.Reorder) {
                if (dropObj) {
                    if (section.ID !== dropObj.SectionID) {
                        for (const item of section.Items) {
                            if (item.ViewState.Guid === viewState.Guid) {
                                return false;
                            }
                        }
                    }
                }
            }

            return true;
        };
    }

    onOpened(group: NavigationGroup, section?: NavigationSection) {
        if (section && !section.Opened) {
            this.store.dispatch(
                NavigationActions.updateSectionState({
                    group: group,
                    section: section,
                })
            );
        } else if (group && !group.Opened) {
            this.store.dispatch(
                NavigationActions.updateGroupState({
                    group: group,
                })
            );
        }

        const id = `${this.application()}-${group.ID.toString()}${section ? `-${this.navigationService.currentPlaybookTeam}-${section.ID}` : ''}`;
        this.visualStateService.saveExpanderSettings('Navigation', id, true);
    }

    onClosed(group: NavigationGroup, section?: NavigationSection) {
        if (section && section.Opened) {
            this.store.dispatch(
                NavigationActions.updateSectionState({
                    group: group,
                    section: section,
                })
            );
        } else if (group && group.Opened) {
            this.store.dispatch(
                NavigationActions.updateGroupState({
                    group: group,
                })
            );
        }

        const id = `${this.application()}-${group.ID.toString()}${section ? `-${this.navigationService.currentPlaybookTeam}-${section.ID}` : ''}`;
        this.visualStateService.saveExpanderSettings('Navigation', id, false);
    }

    navigationItemClick() {
        if (this.visualStateService.shouldPanelsFloat()) {
            this.toggleSideBar.emit();
        }
    }

    trackByGroupId = (_, group: NavigationGroup) => group.ID;
    trackBySectionId = (_, section: NavigationSection) => section.ID;
}
